import React, { useEffect, useState } from 'react';
import cn from "classnames";
import styles from "./ProfileInformation.module.sass";
import Card from "../../../components/Card";
import TextInput from "../../../components/TextInput";
import Dropdown from "../../../components/Dropdown";
import Switch from "../../../components/Switch";
import { getIdccLabel } from '../../../services/convention.service';

// Options pour les menus déroulants
const SECTOR_OPTIONS = [
    { value: 'Technologies & Digital', title: 'Technologies & Digital' },
    { value: 'Finance & Assurance', title: 'Finance & Assurance' },
    { value: 'Santé & Pharmaceutique', title: 'Santé & Pharmaceutique' },
    { value: 'Éducation & Formation', title: 'Éducation & Formation' },
    { value: 'Commerce & Distribution', title: 'Commerce & Distribution' },
    { value: 'Industrie & Manufacturing', title: 'Industrie & Manufacturing' },
    { value: 'Services aux entreprises', title: 'Services aux entreprises' },
    { value: 'Transport & Logistique', title: 'Transport & Logistique' },
    { value: 'Énergie & Environnement', title: 'Énergie & Environnement' },
    { value: 'BTP & Construction', title: 'BTP & Construction' },
    { value: 'Agroalimentaire', title: 'Agroalimentaire' },
    { value: 'Médias & Communication', title: 'Médias & Communication' },
    { value: 'Tourisme & Hôtellerie', title: 'Tourisme & Hôtellerie' },
    { value: 'Autre', title: 'Autre' }
];

const COMPANY_TYPE_OPTIONS = [
    { value: 'TPE', title: 'TPE' },
    { value: 'PME', title: 'PME' },
    { value: 'ETI', title: 'ETI' },
    { value: 'Grande entreprise', title: 'Grande entreprise' },
    { value: 'Start-up', title: 'Start-up' },
    { value: 'Association', title: 'Association' },
    { value: 'Organisme public', title: 'Organisme public' },
    { value: 'Autre', title: 'Autre' }
];

// Options pour les types d'employeur séparées par catégorie
const TYPE_EMPLOYEUR_PRIVE_OPTIONS = [
    { value: 11, title: "11 : Entreprise inscrite au répertoire des métiers ou au registre des entreprises pour l'Alsace-Moselle" },
    { value: 12, title: "12 : Entreprise inscrite uniquement au registre du commerce et des sociétés" },
    { value: 13, title: "13 : Entreprises dont les salariés relèvent de la mutualité sociale agricole" },
    { value: 14, title: "14 : Profession libérale" },
    { value: 15, title: "15 : Association" },
    { value: 16, title: "16 : Autre employeur privé" },
];

const TYPE_EMPLOYEUR_PUBLIC_OPTIONS = [
    { value: 21, title: "21 : Service de l'Etat (administrations centrales et leurs services déconcentrés de la fonction publique d'Etat)" },
    { value: 22, title: "22 : Commune" },
    { value: 23, title: "23 : Département" },
    { value: 24, title: "24 : Région" },
    { value: 25, title: "25 : Etablissement public hospitalier" },
    { value: 26, title: "26 : Etablissement public local d'enseignement" },
    { value: 27, title: "27 : Etablissement public administratif de l'Etat" },
    { value: 28, title: "28 : Etablissement public administratif local(y compris établissement public de coopération intercommunale EPCI)" },
    { value: 29, title: "29 : Autre employeur public" }
];

// Options combinées pour l'initialisation
const TYPE_EMPLOYEUR_OPTIONS = [...TYPE_EMPLOYEUR_PRIVE_OPTIONS, ...TYPE_EMPLOYEUR_PUBLIC_OPTIONS];

const ORGANIZATION_TYPE_OPTIONS = [
    { value: 'Privé', title: 'Privé' },
    { value: 'Public', title: 'Public' }
];

const EMPLOYEUR_SPECIFIQUE_OPTIONS = [
    { value: 0, title: "0 : Aucun de ces cas" },
    { value: 1, title: "1 : Entreprise de travail temporaire" },
    { value: 2, title: "2 : Groupement d'employeurs" },
    { value: 3, title: "3 : Employeur saisonnier" },
    { value: 4, title: "4 : Apprentissage familial" }
];

const CompanyInformation = ({ className, data, updateData, currentStep, validationErrors = {} }) => {
  const [filteredEmployerOptions, setFilteredEmployerOptions] = useState(TYPE_EMPLOYEUR_OPTIONS);
  
  // Utiliser directement les données de l'entreprise transmises par le parent
  const companyData = data?.company || {};
  
  // Fusionner les données de l'entreprise avec les modifications en cours
  const currentData = { ...companyData, ...data.companyInfo };

  // Gestionnaires pour les changements de données
  const handleCompanyChange = (e) => {
    const { name, value } = e.target;
    
    // Mettre à jour les données de l'entreprise
    if (updateData) {
      updateData('companyInfo', { ...data.companyInfo, [name]: value });
    }
  };

  const handleDropdownChange = (name, value) => {
    // Mettre à jour les données de l'entreprise
    if (updateData) {
      updateData('companyInfo', { ...data.companyInfo, [name]: value });
    }
  };

  const handleSwitchChange = (name, value) => {
    // Mettre à jour les données de l'entreprise
    if (updateData) {
      updateData('companyInfo', { ...data.companyInfo, [name]: value });
    }
  };

  // Déterminer automatiquement le type d'organisation en fonction du type d'employeur
  useEffect(() => {
    if (currentData?.type_of_employer && !currentData?.organization_type) {
      const employerType = Number(currentData.type_of_employer);
      let organizationType = null;
      
      if (employerType >= 11 && employerType <= 16) {
        organizationType = 'Privé';
      } else if (employerType >= 21 && employerType <= 29) {
        organizationType = 'Public';
      }
      
      if (organizationType) {
        handleDropdownChange('organization_type', organizationType);
      }
    }
  }, [currentData?.type_of_employer, currentData?.organization_type]);
  
  // Effet pour récupérer automatiquement le label IDCC lorsque le code IDCC est saisi
  useEffect(() => {
    const fetchIdccLabel = async () => {
      if (currentData?.idcc_code) {
        try {
          const conventionData = await getIdccLabel(currentData.idcc_code);
          
          if (conventionData && conventionData.title) {
            handleDropdownChange('Idcc_label', conventionData.title);
          }
        } catch (error) {
          console.error('Erreur lors de la récupération automatique du label IDCC:', error);
        }
      }
    };
    
    fetchIdccLabel();
  }, [currentData?.idcc_code]);

  // Effet pour filtrer les options de type d'employeur en fonction du type d'organisation
  useEffect(() => {
    if (currentData?.organization_type === 'Privé') {
      setFilteredEmployerOptions(TYPE_EMPLOYEUR_PRIVE_OPTIONS);
    } else if (currentData?.organization_type === 'Public') {
      setFilteredEmployerOptions(TYPE_EMPLOYEUR_PUBLIC_OPTIONS);
    } else {
      setFilteredEmployerOptions(TYPE_EMPLOYEUR_OPTIONS);
    }
  }, [currentData?.organization_type]);
  

  return (
    <Card className={cn(styles.card, className)} title="Informations de l'entreprise" classTitle="title-green">
      <div className={styles.group}>
        <TextInput
          className={styles.field}
          label="Nom de l'entreprise"
          name="name"
          type="text"
          placeholder="Nom de l'entreprise"
          value={currentData?.name || ''}
          onChange={handleCompanyChange}
          required
          error={validationErrors.name}
        />
        <TextInput
          className={styles.field}
          label="Dénomination sociale"
          name="corporate_name"
          type="text"
          placeholder="Dénomination sociale"
          value={currentData?.corporate_name || ''}
          onChange={handleCompanyChange}
          required
          error={validationErrors.corporate_name}
        />
        <div className={styles.field}>
          <Dropdown
            className={styles.dropdown}
            label="Secteur d'activité"
            name="sector"
            required
            options={SECTOR_OPTIONS}
            setValue={(option) => handleDropdownChange('sector', option.value)}
            value={SECTOR_OPTIONS.find(opt => opt.value === currentData?.sector) || ''}
            error={validationErrors.sector}
          />
        </div>
        <div className={styles.field}>
          <Dropdown
            className={styles.dropdown}
            label="Type d'entreprise"
            name="type_of_company"
            options={COMPANY_TYPE_OPTIONS}
            setValue={(option) => handleDropdownChange('type_of_company', option.value)}
            value={COMPANY_TYPE_OPTIONS.find(opt => opt.value === currentData?.type_of_company) || ''}
            error={validationErrors.type_of_company}
          />
        </div>
        <div className={styles.field}>
          <Dropdown
            className={styles.dropdown}
            label="Type d'organisation"
            name="organization_type"
            options={ORGANIZATION_TYPE_OPTIONS}
            setValue={(option) => {
              // Mettre à jour le type d'organisation
              handleDropdownChange('organization_type', option.value);
              
              // Réinitialiser le type d'employeur si le type d'organisation change
              if ((option.value === 'Privé' && Number(currentData?.type_of_employer) >= 21) ||
                  (option.value === 'Public' && Number(currentData?.type_of_employer) <= 16)) {
                handleDropdownChange('type_of_employer', null);
              }
            }}
            value={ORGANIZATION_TYPE_OPTIONS.find(opt => opt.value === currentData?.organization_type) || ''}
            error={validationErrors.organization_type}
          />
        </div>

        <div className={styles.field}>
          <Dropdown
            className={styles.dropdown}
            label="Type d'employeur"
            name="type_of_employer"
            options={filteredEmployerOptions}
            setValue={(option) => handleDropdownChange('type_of_employer', option.value)}
            value={TYPE_EMPLOYEUR_OPTIONS.find(opt => Number(opt.value) === Number(currentData?.type_of_employer)) || ''}
            error={validationErrors.type_of_employer}
          />
        </div>
        <div className={styles.field}>
          <Dropdown
            className={styles.dropdown}
            label="Employeur spécifique"
            name="specific_employer"
            options={EMPLOYEUR_SPECIFIQUE_OPTIONS}
            setValue={(option) => handleDropdownChange('specific_employer', option.value)}
            value={EMPLOYEUR_SPECIFIQUE_OPTIONS.find(opt => Number(opt.value) === Number(currentData?.specific_employer)) || ''}
            error={validationErrors.specific_employer}
          />
        </div>
        <TextInput
          className={styles.field}
          label="Nombre d'employés"
          name="employee_count"
          type="number"
          placeholder="Nombre d'employés"
          value={currentData?.employee_count || ''}
          onChange={handleCompanyChange}
          required
          error={validationErrors.employee_count}
        />
        <TextInput
          className={styles.field}
          label="Siret"
          name="siret"
          type="text"
          placeholder="Siret"
          value={currentData?.siret || ''}
          onChange={handleCompanyChange}
          error={validationErrors.siret}
        />
        <TextInput
          className={styles.field}
          label="Code NAF / APE"
          name="naf_code"
          type="text"
          placeholder="Code NAF"
          value={currentData?.naf_code || ''}
          onChange={handleCompanyChange}
          error={validationErrors.naf_code}
        />
        <TextInput
          className={styles.field}
          label="Code IDCC"
          name="idcc_code"
          type="text"
          placeholder="Code IDCC"
          value={currentData?.idcc_code || ''}
          onChange={handleCompanyChange}
          error={validationErrors.idcc_code}
        />
        <TextInput
          className={styles.field}
          label="Label IDCC"
          name="Idcc_label"
          type="text"
          placeholder="Label IDCC"
          value={currentData?.Idcc_label || ''}
          onChange={handleCompanyChange}
          error={validationErrors.Idcc_label}
        />
        <TextInput
          className={styles.field}
          label="Caisse des retraites"
          name="supplementary_pension_fund"
          type="text"
          placeholder="Caisse des retraites"
          value={currentData?.supplementary_pension_fund || ''}
          onChange={handleCompanyChange}
          error={validationErrors.supplementary_pension_fund}
        />
        <div className={styles.field} style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ marginLeft: '10px' }}>Régime spécifique :</span>
          <Switch
            name="specific_diet"
            value={currentData?.specific_diet || false}
            onChange={(value) => handleSwitchChange('specific_diet', value)}
            className={styles.switch}
          />
        </div>
      </div>
    </Card>
  );
};

export default CompanyInformation;
